import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from './lib/supabase'

type Mode = 'request' | 'recovery' | 'sent' | 'done' | 'blocked'

function isRecoveryUrl(url: URL): boolean {
  const typeParam = url.searchParams.get('type')
  if (typeParam?.toLowerCase() === 'recovery') return true

  // Some setups use hash fragments for auth params
  const hash = url.hash || ''
  return hash.includes('type=recovery')
}

export default function ResetPassword() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()

  const initialUrl = useMemo(() => new URL(window.location.href), [])
  const initialErrorFromUrl = useMemo(() => {
    const hashParams = new URLSearchParams((initialUrl.hash || '').replace(/^#/, ''))
    const errorCode = initialUrl.searchParams.get('error_code') || hashParams.get('error_code')
    const errorDescription =
      initialUrl.searchParams.get('error_description') || hashParams.get('error_description')
    if (!errorCode) return null
    const desc = errorDescription ? decodeURIComponent(errorDescription) : null
    return desc || `Authentication error: ${errorCode}`
  }, [initialUrl])
  const [mode, setMode] = useState<Mode>(() => {
    if (initialUrl.searchParams.get('sent') === '1') return 'sent'
    return 'request'
  })

  const [email, setEmail] = useState(() => initialUrl.searchParams.get('email') ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(initialErrorFromUrl)

  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')

  useEffect(() => {
    let isMounted = true

    const init = async () => {
      try {
        // PKCE recovery links include `?code=...` and require exchanging for a session.
        // We do this opportunistically; if there's no code, Supabase no-ops.
        const url = new URL(window.location.href)
        const hashParams = new URLSearchParams((url.hash || '').replace(/^#/, ''))
        const errCode = url.searchParams.get('error_code') || hashParams.get('error_code')
        const errDesc = url.searchParams.get('error_description') || hashParams.get('error_description')
        if (errCode) {
          if (!isMounted) return
          setError(errDesc ? decodeURIComponent(errDesc) : `Authentication error: ${errCode}`)
          setMode('request')
          // Clean up noisy params
          url.searchParams.delete('error')
          url.searchParams.delete('error_code')
          url.searchParams.delete('error_description')
          url.hash = ''
          window.history.replaceState({}, document.title, url.toString())
          return
        }

        const hasCode = !!url.searchParams.get('code')
        if (hasCode) {
          // If the link was requested on a different origin (www vs non-www), the PKCE code_verifier
          // will be missing. Bounce to the original origin if we have it.
          const storedOrigin = localStorage.getItem('th24_reset_origin')
          if (storedOrigin && storedOrigin !== window.location.origin) {
            const next = new URL(window.location.href)
            next.protocol = new URL(storedOrigin).protocol
            next.host = new URL(storedOrigin).host
            window.location.replace(next.toString())
            return
          }

          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(window.location.href)
          if (exchangeError) {
            // If exchange fails (common when link is opened in an in-app browser / different device),
            // fall back to request mode so user can request a fresh link.
            if (!isMounted) return
            setError(
              exchangeError.message.includes('code verifier') ||
                exchangeError.message.includes('code and code verifier')
                ? 'Reset link doğrulanamadı. En sık sebep www/non-www alan adı farkı veya tarayıcı site verilerinin temizlenmiş olması. Lütfen bu tarayıcıdan yeniden şifre sıfırlama linki isteyin.'
                : exchangeError.message,
            )
            setMode('request')
            return
          } else {
            // Remove code from address bar
            url.searchParams.delete('code')
            url.searchParams.delete('type')
            window.history.replaceState({}, document.title, url.toString())
          }
        }

        const recovery = isRecoveryUrl(url)
        const { data } = await supabase.auth.getSession()
        const session = data.session

        if (!isMounted) return

        if (recovery) {
          setMode('recovery')
          return
        }

        // If user is already signed in normally, this page isn't needed.
        if (session) {
          setMode('blocked')
          return
        }
      } catch (e: any) {
        if (!isMounted) return
        setError(e?.message || t('login.errorOccurred'))
      }
    }

    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (!isMounted) return
      if (event === 'PASSWORD_RECOVERY') setMode('recovery')
    })

    init()

    return () => {
      isMounted = false
      subscription.subscription.unsubscribe()
    }
  }, [t])

  const redirectTo = `${window.location.origin}/reset-password`

  const handleSendResetLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })
      if (resetError) {
        setError(resetError.message)
        return
      }

      setMode('sent')
      const url = new URL(window.location.href)
      url.searchParams.set('sent', '1')
      if (email) url.searchParams.set('email', email)
      window.history.replaceState({}, document.title, url.toString())
    } catch (e: any) {
      setError(e?.message || t('login.errorOccurred'))
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (newPassword.length < 6) {
      setError(t('login.passwordTooShort'))
      return
    }
    if (newPassword !== confirmNewPassword) {
      setError(t('login.passwordMismatch'))
      return
    }

    setLoading(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
      if (updateError) {
        setError(updateError.message)
        return
      }

      // Clean finish: sign out and send user back to login modal
      await supabase.auth.signOut()
      setMode('done')
      window.location.href = '/?login=1'
    } catch (e: any) {
      setError(e?.message || t('login.errorOccurred'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-app-bg flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-app-box rounded-2xl shadow-xl p-6 md:p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">{t('login.resetPassword')}</h1>
        <p className="text-sm text-gray-600 mb-6">{t('login.resetPasswordDescription')}</p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {mode === 'blocked' && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
              {t('login.alreadySignedInReset')}
            </div>
            <button
              onClick={() => navigate('/app')}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Go to Home
            </button>
          </div>
        )}

        {mode === 'request' && (
          <form onSubmit={handleSendResetLink} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('login.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={t('login.emailPlaceholder')}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('login.sendingLink') : t('login.sendResetLink')}
            </button>

            <button
              type="button"
              onClick={() => (window.location.href = '/?login=1')}
              className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 px-4 transition-colors"
            >
              {t('login.backToLogin')}
            </button>
          </form>
        )}

        {mode === 'sent' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {t('login.resetLinkSent')}
            </div>
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
              {t('login.checkSpamFolder')}
            </div>

            <button
              type="button"
              onClick={() => (window.location.href = '/?login=1')}
              className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 px-4 transition-colors"
            >
              {t('login.backToLogin')}
            </button>
          </div>
        )}

        {mode === 'recovery' && (
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                {t('login.newPassword')}
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={t('login.newPasswordPlaceholder')}
                autoComplete="new-password"
              />
            </div>

            <div>
              <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700 mb-2">
                {t('login.confirmNewPassword')}
              </label>
              <input
                id="confirmNewPassword"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder={t('login.confirmNewPasswordPlaceholder')}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('login.updatingPassword') : t('login.updatePassword')}
            </button>
          </form>
        )}

        {mode === 'done' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
              {t('login.passwordUpdated')}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

